import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { analytics } from '../../services/analytics';
import SEOPageLayout from '../../components/SEOPageLayout';

const PoolConstructionPackages = () => {
  useEffect(() => {
    analytics.track('Template Page View', { template: 'Pool Construction Quote Template' });
  }, []);

  return (
    <SEOPageLayout
      title="Free Pool Construction Quote Template - Tiered Options (Good/Better/Best) | QuoteTier"
      description="Free pool construction quote template with Good/Better/Best packages. From basic inground pool to full oasis with spa, all priced in tiers."
      canonical="/templates/pool-construction-packages-template"
      ogTitle="Free Pool Construction Quote Template"
      ogDescription="Pool buyers making a $40K decision will often say yes to $60K if the $60K option is on the same page. Template included."
    >
      <section className="py-32 md:py-40 lg:py-48">
        <div className="container max-w-7xl mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="font-instrument text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight leading-tight mb-6">
              Free <span className="text-primary font-bold">Pool Construction Quote Template</span>
            </h1>
            <p className="font-jetbrains text-xl md:text-2xl text-gray-600 tracking-tight leading-relaxed mb-8">
              From a basic inground pool to a full oasis with spa and premium finish — the quote structure that captures upsells before construction starts.
            </p>
            <div className="max-w-2xl mx-auto mb-10 p-5 border border-gray-200 rounded-lg bg-gray-50 text-left">
              <p className="font-jetbrains text-sm md:text-base text-gray-700 leading-relaxed">
                Pool buyers comparing quotes are making a $40K–$80K decision, and most don't know what the $40K version is missing until they see the $80K one. Three tiers — basic pool, pool + spa, luxury oasis — anchor the decision around scope, not just price.
              </p>
            </div>
            <div className="font-instrument mt-8 flex flex-col sm:flex-row gap-6 justify-center items-center mb-3">
              <Link to="/signup">
                <Button className="px-8 py-4 text-xl font-semibold bg-black text-white hover:bg-gray-800 rounded-lg shadow-lg">
                  Use This Template Free
                </Button>
              </Link>
            </div>
            <p className="font-jetbrains text-sm text-gray-500 mb-6">Free account — no credit card required.</p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-4">Sample Tiers</h2>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            <Card className="border border-black">
              <CardHeader><CardTitle className="font-instrument text-xl">Basic Pool</CardTitle><CardDescription>The Budget Option</CardDescription></CardHeader>
              <CardContent>
                  <ul className="font-jetbrains text-sm text-gray-600 list-disc list-inside">
                      <li>12x24 vinyl liner pool</li>
                      <li>Basic decking (concrete)</li>
                      <li>1HP pump & sand filter</li>
                  </ul>
              </CardContent>
            </Card>
            <Card className="border border-black bg-black text-white">
              <CardHeader><CardTitle className="font-instrument text-xl text-white">Pool + Spa</CardTitle><CardDescription className="text-gray-300">Best Seller</CardDescription></CardHeader>
              <CardContent>
                  <ul className="font-jetbrains text-sm text-gray-300 list-disc list-inside">
                      <li>16x32 gunite pool + spa</li>
                      <li>Stamped concrete deck</li>
                      <li>LED lighting package</li>
                      <li>Variable-speed pump</li>
                  </ul>
              </CardContent>
            </Card>
            <Card className="border border-black">
              <CardHeader><CardTitle className="font-instrument text-xl">Luxury Oasis</CardTitle><CardDescription>Resort Style</CardDescription></CardHeader>
              <CardContent>
                  <ul className="font-jetbrains text-sm text-gray-600 list-disc list-inside">
                      <li>Custom freeform design</li>
                      <li>Travertine coping & deck</li>
                      <li>Spa + waterfall features</li>
                      <li>Automation & salt system</li>
                  </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-black text-white">
        <div className="container max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-6">Start Using This Template Now</h2>
          <Link to="/signup">
            <Button className="px-10 py-4 font-instrument text-xl font-semibold bg-white text-black hover:bg-gray-200 rounded-lg shadow-lg">
              Get Free Access
            </Button>
          </Link>
        </div>
      </section>
    </SEOPageLayout>
  );
};

export default PoolConstructionPackages;
